import DOMPurify from 'isomorphic-dompurify';

import { getImage } from 'gatsby-plugin-image';
import { graphql, navigate, PageProps } from 'gatsby';

import '../styles/generic.css';
import '../styles/homepage.css';

import platformLayers from '../assets/images/platform-layers-left.svg';
import { _ } from '../utils/l10n';
import { Button } from '../components/generic/Button';
import { DetailSummaryItem } from '../components/DetailSummaryItem';
import { Layout } from '../components/layout'
import { Partners } from '../components/homePage/Partners';
import { SolutionOverviewItem } from '../components/solutions/SolutionOverviewItem';
import { Seo } from '../components/seo'
import { RecursiveNonNullable } from '../types';
import { NewsHighlightsCarousel } from '../components/homePage/NewsHighlightsCarousel';
import { UseCaseTile } from '../components/use-cases/UseCaseTile';
import { NativeStoreLinks } from '../components/NativeStoreLinks';
import { ButtonSizes } from '../consts/button';

const IndexPage = ({ data } : PageProps<RecursiveNonNullable<Queries.HomePageQuery>>) => {
  const { use_case_types, solutions, partnership } = data?.file?.childMdx?.frontmatter || {};

  return (
    <Layout isHomePage contentClassName='homepage-content'>
      <Seo title='Home' />
      <NewsHighlightsCarousel />
      <Partners />
      {/* @todo separate into component */}
      <section className='homepage-details-info'>
        <div className='homepage-item-right'>
          <DetailSummaryItem
            title={use_case_types?.title ?? ''}
            description={use_case_types?.body ?? ''}
            button={{
              label: use_case_types?.button_label ?? '',
            }}
            onClick={() => navigate('/use-cases/')}
          />
          <div className='homepage-use-cases-overview'>
            {use_case_types.overview.slice(0, 4).map((use_case_type) => {
              const {
                title, image, is_image_dark, description, anchor
              } = use_case_type;
              return (
                <UseCaseTile
                  key={title}
                  title={title}
                  description={description}
                  link={`/use-cases/#${anchor}`}
                  image={getImage(image)}
                  isImageDark={is_image_dark}
                  imageLoading='eager'
                />
              );
            })}
          </div>
          <Button
            label={use_case_types?.button_label ?? ''}
            className='homepage-button-bottom'
            size={ButtonSizes.LARGE}
            onClick={() => navigate('/use-cases/')}
          />
        </div>
      </section>
      {/* @todo separate into component */}
      <section className='homepage-solutions-section'>
        <div className='homepage-solutions'>
          <h2 className="detail-summary-item-title" dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(solutions.title) }} />
          <ul className='homepage-solutions-overview'>
            {solutions.overview.map((solution, index) => {
              const {
                title, image, image_alt, description, url
              } = solution;
              return (
                <SolutionOverviewItem
                  key={title}
                  title={title}
                  description={description}
                  image={getImage(image)}
                  imageAlt={image_alt}
                  link={{href: url}}
                  isTileClickable={false}
                >
                  {index == 0 && (
                    <NativeStoreLinks
                    appStoreLink='https://apps.apple.com/us/app/twex/id1556701595'
                    googlePlayLink='https://play.google.com/store/apps/details?id=io.trustwise.twex'
                    />
                  )}
                </SolutionOverviewItem>
              );
            })}
          </ul>
          <Button
            label={solutions?.button_label ?? ''}
            className=''
            size={ButtonSizes.LARGE}
            onClick={() => navigate('/solutions/')}
          />
        </div>
      </section>
      <section className='homepage-platform-section' >
        <div className='homepage-item-left'>
          <div className='homepage-item-left-layers'>
            <img src={platformLayers} />
          </div>
          <DetailSummaryItem
            title={partnership?.title ?? ''}
            description={partnership?.body ?? ''}
            button={{
              label: partnership?.button_label ?? '',
            }}
            onClick={() => navigate('/platform/build-your-solution/')}
          />
        </div>
      </section>
    </Layout>
  );
};

export default IndexPage;

export const Head = () => <body className="page-home" />

// @todo consider using static query as there are no variables needed
// @todo consider defining fragment separately for better reusability
export const query = graphql`
query HomePage {
  file(
    sourceInstanceName: {eq: "content"}
    relativeDirectory: {eq: ""}
    name: {eq: "index"}
  ) {
    childMdx {
      frontmatter {
        use_case_types {
          title
          body
          button_label
          overview {
            title
            image {
              childImageSharp {
                gatsbyImageData(placeholder: DOMINANT_COLOR)
              }
            }
            image_alt
            is_image_dark
            description
            anchor
          }
        }
        solutions {
          title
          button_label
          overview {
            title
            description
            url
            image {
              childImageSharp {
                gatsbyImageData(placeholder: NONE)
              }
            }
            image_alt
          }
        }
        partnership {
          title
          body
          button_label
        }
      }
    }
  }
}
`;
